import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog, POSTHOG_EVENTS } from '../../../components/analytics/PostHogProvider';


const EHRConnect = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { captureEvent } = usePostHog();

  // Track EHR connect page view
  useEffect(() => {
    captureEvent(POSTHOG_EVENTS.EHR_CONNECT_STARTED);
  }, [captureEvent]);

  const totalSteps = 4;

  const ehrVendors = [
    {
      id: 'epic',
      name: 'Epic Systems',
      subtitle: 'Epic MyWay, Hyperspace',
      icon: '🏥',
      color: '#8b5cf6'
    },
    {
      id: 'cerner',
      name: 'Cerner',
      subtitle: 'PowerChart, Millennium',
      icon: '⚕️',
      color: '#3b82f6'
    },
    {
      id: 'allscripts',
      name: 'Allscripts',
      subtitle: 'TouchWorks, Professional',
      icon: '📋',
      color: '#10b981'
    },
    {
      id: 'athenahealth',
      name: 'Athenahealth',
      subtitle: 'athenaClinicals, athenaOne',
      icon: '🏥',
      color: '#06b6d4'
    },
    {
      id: 'eclinicalworks',
      name: 'eClinicalWorks',
      subtitle: 'EHR, PRAXIS',
      icon: '📊',
      color: '#f59e0b'
    },
    {
      id: 'other',
      name: 'Other',
      subtitle: 'Custom EHR system',
      icon: '⚙️',
      color: '#6b7280'
    }
  ];

  const filteredVendors = ehrVendors.filter(vendor =>
    vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    vendor.subtitle.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleVendorSelect = (vendorId) => {
    setSelectedVendor(vendorId);
  };

  const handleNext = () => {
    if (currentStep === 1 && selectedVendor) {
      // Navigate to providers upload page after selecting vendor
      navigate('/onboarding/providers-upload');
    } else if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/onboarding/dashboard');
  };

  const progressPercentage = (currentStep / totalSteps) * 100;

  return (
    <div className="min-vh-100 bg-light" style={{ minHeight: '100vh', background: '#f1f5f9', padding: '2rem 4rem' }}>
      <div className="mx-auto" style={{ maxWidth: 1000, margin: '0 auto' }}>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4" style={{ marginBottom: '1.5rem' }}>
          <h1 className="h1 fw-bold text-dark mb-0" style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>EHR Connect Wizard</h1>
          <div className="d-flex align-items-center gap-2" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'transparent', padding: 0 }}>
            <span className="fs-5" style={{ fontSize: '1.25rem' }}>⭐</span>
            <span className="fw-bold text-warning mb-0" style={{ fontSize: '1rem', fontWeight: 700, color: '#f59e0b', margin: 0 }}>200 XP</span>
            <span className="text-muted small" style={{ fontSize: '0.875rem', color: '#94a3b8' }}>upon completion</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4" style={{ marginBottom: '2rem' }}>
          <div className="progress" style={{ height: 6, background: '#e2e8f0', borderRadius: 3, marginBottom: '0.5rem' }}>
            <div 
              className="progress-bar"
              style={{ 
                height: '100%', 
                background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)', 
                borderRadius: 4, 
                width: `${progressPercentage}%`,
                transition: 'width 0.3s ease'
              }}
            />
          </div>
          <div className="text-end small text-muted" style={{ textAlign: 'right', fontSize: '0.875rem', color: '#64748b', fontWeight: 500 }}>Step {currentStep} of {totalSteps}</div>
        </div>

        {/* Main Card */}
        <div className="card border-0 shadow-sm rounded-4 overflow-hidden" style={{ background: 'white', borderRadius: 16, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
          {currentStep === 1 && (
            <>
              <div className="p-5" style={{ padding: '3rem' }}>
                <h2 className="h2 fw-bold text-dark mb-2" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', margin: '0 0 0.5rem 0' }}>Select Your EHR Vendor</h2>
                <p className="text-muted mb-4" style={{ fontSize: '1rem', color: '#64748b', margin: '0 0 2rem 0' }}>
                  Choose your Electronic Health Records system from the list below
                </p>

                {/* Search Bar */}
                <div className="position-relative mb-4" style={{ position: 'relative', marginBottom: '2rem' }}>
                  <span className="position-absolute start-0 top-50 translate-middle-y ms-3 text-muted" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', fontSize: '1.125rem', color: '#94a3b8' }}>🔍</span>
                  <input
                    type="text"
                    className="form-control ps-5"
                    style={{ 
                      width: '100%', 
                      padding: '0.875rem 1rem 0.875rem 3rem', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: 8, 
                      fontSize: '0.9375rem', 
                      color: '#1e293b', 
                      transition: 'all 0.2s' 
                    }}
                    placeholder="Search for your EHR vendor..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* Vendor Grid */}
                <div className="row g-3 mb-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                  {filteredVendors.map((vendor) => (
                    <div
                      key={vendor.id}
                      className={`d-flex align-items-center gap-3 p-3 border rounded-3 cursor-pointer transition-all ${
                        selectedVendor === vendor.id 
                          ? 'border-primary bg-light' 
                          : 'border-secondary-subtle'
                      }`}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '1rem', 
                        padding: '1.25rem', 
                        border: '2px solid #e2e8f0', 
                        borderRadius: 10, 
                        background: 'white', 
                        cursor: 'pointer', 
                        transition: 'all 0.2s',
                        ...(selectedVendor === vendor.id && {
                          borderColor: '#3b82f6',
                          background: '#eff6ff',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
                        })
                      }}
                      onClick={() => handleVendorSelect(vendor.id)}
                    >
                      <div 
                        className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                        style={{ 
                          width: 48, 
                          height: 48, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          borderRadius: 10, 
                          fontSize: '1.5rem', 
                          flexShrink: 0,
                          backgroundColor: `${vendor.color}20`, 
                          color: vendor.color 
                        }}
                      >
                        {vendor.icon}
                      </div>
                      <div className="flex-grow-1 min-w-0">
                        <h3 className="h6 fw-semibold text-dark mb-1" style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b', margin: '0 0 0.25rem 0' }}>{vendor.name}</h3>
                        <p className="text-muted mb-0 small" style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>{vendor.subtitle}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Security Note */}
                <div className="d-flex gap-3 p-3 bg-warning bg-opacity-10 border border-warning rounded-3 mt-4" style={{ 
                  display: 'flex', 
                  gap: '1rem', 
                  padding: '1rem', 
                  background: '#fef3c7', 
                  border: '1px solid #fbbf24', 
                  borderRadius: 12, 
                  marginTop: '2rem' 
                }}>
                  <span className="fs-5 flex-shrink-0" style={{ fontSize: '1.25rem', flexShrink: 0 }}>⚠️</span>
                  <div className="flex-grow-1">
                    <strong className="d-block text-warning-emphasis mb-1" style={{ 
                      display: 'block', 
                      fontSize: '0.9375rem', 
                      color: '#92400e', 
                      marginBottom: '0.25rem' 
                    }}>IT Security Note</strong>
                    <p className="text-warning-emphasis mb-0 small" style={{ 
                      fontSize: '0.875rem', 
                      color: '#78350f', 
                      margin: 0, 
                      lineHeight: 1.5 
                    }}>
                      If your EHR uses an IT-managed firewall, please ask IT to whitelist domain: 
                      <code className="bg-warning bg-opacity-25 px-1 py-0 rounded" style={{ 
                        background: '#fde68a', 
                        padding: '0.125rem 0.375rem', 
                        borderRadius: 4, 
                        fontFamily: 'Monaco, Courier New, monospace', 
                        fontSize: '0.8125rem', 
                        color: '#92400e' 
                      }}>api.ankr.example</code>
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Footer Actions */}
          <div className="d-flex justify-content-between align-items-center p-4 border-top bg-light" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '1.5rem 3rem', 
            background: '#fafbfc', 
            borderTop: '1px solid #e2e8f0' 
          }}>
            <button 
              className="btn btn-light btn-md"
              style={{ 
                padding: '0.75rem 1.5rem', 
                borderRadius: 8, 
                fontSize: '0.9375rem', 
                fontWeight: 600, 
                cursor: 'pointer', 
                transition: 'all 0.2s', 
                border: '1px solid #e2e8f0', 
                background: 'white', 
                color: '#64748b' 
              }}
              onClick={handleBackToDashboard}
              onMouseEnter={(e) => {
                e.target.style.background = '#f8fafc';
                e.target.style.color = '#475569';
                e.target.style.borderColor = '#cbd5e1';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white';
                e.target.style.color = '#64748b';
                e.target.style.borderColor = '#e2e8f0';
              }}
            >
              ← Back to Dashboard
            </button>
            <button 
              className={`btn btn-primary btn-md ${!selectedVendor ? 'disabled' : ''}`}
              style={{ 
                padding: '0.75rem 1.5rem', 
                borderRadius: 8, 
                fontSize: '0.9375rem', 
                fontWeight: 600, 
                cursor: 'pointer', 
                transition: 'all 0.2s', 
                border: 'none',
                background: selectedVendor ? '#3b82f6' : '#cbd5e1',
                color: selectedVendor ? 'white' : '#94a3b8'
              }}
              onClick={handleNext}
              disabled={!selectedVendor}
              onMouseEnter={(e) => {
                if (selectedVendor) {
                  e.target.style.background = '#2563eb';
                  e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedVendor) {
                  e.target.style.background = '#3b82f6';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EHRConnect;
